import { create } from 'zustand';

export interface HomeTile {
  id: string;
  icon: string;
  label: string;
  path: string;
  color: string;
  visible: boolean;
  description?: string;
  category?: 'quickAction' | 'main' | 'more';
}

// Consistent color categories:
// Planning (purple): #9575cd — Calendar, Timetable, Today
// Tasks & Lists (green): #66bb6a — Tasks, Lists, Pantry
// Communication (teal): #4db6ac — Chat, Feed, Contacts
// Finance (amber): #ffb74d — Budget, Subs
// Household (blue): #64b5f6 — Meals, Recipes, Docs
// School (indigo): #7986cb — School, Inbox
// Health & Safety (rose): #ef5350 — Health, Map
// Special (coral): #ff8a65 — Special Days

const DEFAULT_TILES: HomeTile[] = [
  // Quick Actions (4 featured cards on desktop)
  { id: 'lists', icon: 'shopping_cart', label: 'Shopping', path: '/app/lists', color: '#e9c46a', visible: true, description: 'Shopping and to-do lists.', category: 'quickAction' },
  { id: 'calendar', icon: 'calendar_month', label: 'Calendar', path: '/app/calendar', color: '#b39ddb', visible: true, description: 'View schedule, events and reminders.', category: 'quickAction' },
  { id: 'chat', icon: 'chat_bubble', label: 'Chat', path: '/app/chat', color: '#80cbc4', visible: true, description: 'Message family, friends or your team.', category: 'quickAction' },
  { id: 'kitchen', icon: 'restaurant', label: 'Kitchen', path: '/app/kitchen', color: '#4fc3f7', visible: true, description: 'Meal plans and recipes.', category: 'quickAction' },
  // Everything else
  { id: 'tasks', icon: 'check_circle', label: 'Tasks', path: '/app/tasks', color: '#81c784', visible: true, description: 'Shared family to-dos.', category: 'main' },
  { id: 'budget', icon: 'account_balance_wallet', label: 'Budget', path: '/app/budget', color: '#4fc3f7', visible: true, description: 'Track spending, set goals and stay in control.', category: 'main' },
  { id: 'pantry', icon: 'inventory_2', label: 'Pantry', path: '/app/pantry', color: '#a5d6a7', visible: true, description: 'What is in the cupboard, fridge and freezer.', category: 'main' },
  { id: 'contacts', icon: 'contacts', label: 'Contacts', path: '/app/contacts', color: '#f48fb1', visible: true, description: 'Manage your important people.', category: 'main' },
  { id: 'docs', icon: 'folder_open', label: 'Docs', path: '/app/documents', color: '#64b5f6', visible: true, description: 'Access and organise your documents.', category: 'main' },
  { id: 'health', icon: 'local_hospital', label: 'Health', path: '/app/health', color: '#ef5350', visible: true, description: 'Track wellness for you and your family.', category: 'main' },
  { id: 'school', icon: 'school', label: 'School', path: '/app/school-calendar', color: '#b39ddb', visible: true, description: 'Stay on top of school life.', category: 'main' },
  { id: 'map', icon: 'location_on', label: 'Map', path: '/app/locator', color: '#80deea', visible: true, description: 'Find places, save locations, get directions.', category: 'main' },
  { id: 'special-days', icon: 'star', label: 'Special Days', path: '/app/special-days', color: '#ff8a65', visible: true, description: 'Birthdays, anniversaries and important dates.', category: 'main' },
  { id: 'reminders', icon: 'notifications', label: 'Reminders', path: '/app/reminders', color: '#ffb74d', visible: true, description: 'Recurring reminders for anything.', category: 'main' },
  { id: 'discover', icon: 'location_on', label: 'Discover', path: '/app/discover', color: '#80cbc4', visible: true, description: 'Find nearby family days out and activities.', category: 'main' },
];

interface HomeLayoutState {
  tiles: HomeTile[];
  setupDone: boolean;
  maxVisible: number | null; // null = show all
  setTiles: (tiles: HomeTile[]) => void;
  moveTile: (fromIndex: number, toIndex: number) => void;
  toggleTile: (id: string) => void;
  bulkSetVisibility: (visibleIds: Set<string>) => void;
  markSetupDone: () => void;
  resetLayout: () => void;
  setMaxVisible: (n: number | null) => void;
}

const REMOVED_TILES = new Set(['family', 'timetable', 'subs', 'wishlist', 'notes', 'polls', 'inbox', 'meals', 'recipes', 'feed', 'today']);

const defaultsById = new Map(DEFAULT_TILES.map(t => [t.id, t]));

const loadTiles = (): HomeTile[] => {
  try {
    const saved = localStorage.getItem('fh-home-layout');
    if (saved) {
      const parsed = (JSON.parse(saved) as HomeTile[]).filter(t => !REMOVED_TILES.has(t.id));
      // Merge saved tiles with defaults to pick up new fields (category, description)
      const merged = parsed.map(t => {
        const def = defaultsById.get(t.id);
        return def ? { ...def, ...t, category: def.category, description: def.description } : t;
      });
      const savedIds = new Set(merged.map(t => t.id));
      const newTiles = DEFAULT_TILES.filter(t => !savedIds.has(t.id));
      return [...merged, ...newTiles];
    }
  } catch {}
  return DEFAULT_TILES;
};

const loadMaxVisible = (): number | null => {
  const raw = localStorage.getItem('fh-home-max-visible');
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export const useHomeLayoutStore = create<HomeLayoutState>((set, get) => ({
  tiles: loadTiles(),
  setupDone: localStorage.getItem('fh-home-setup-done') === 'true',
  maxVisible: loadMaxVisible(),

  setTiles: (tiles) => {
    localStorage.setItem('fh-home-layout', JSON.stringify(tiles));
    set({ tiles });
  },

  moveTile: (fromIndex, toIndex) => {
    const tiles = [...get().tiles];
    const [moved] = tiles.splice(fromIndex, 1);
    tiles.splice(toIndex, 0, moved);
    localStorage.setItem('fh-home-layout', JSON.stringify(tiles));
    set({ tiles });
  },

  toggleTile: (id) => {
    const tiles = get().tiles.map(t =>
      t.id === id ? { ...t, visible: !t.visible } : t
    );
    localStorage.setItem('fh-home-layout', JSON.stringify(tiles));
    set({ tiles });
  },

  bulkSetVisibility: (visibleIds) => {
    const tiles = get().tiles.map(t => ({ ...t, visible: visibleIds.has(t.id) }));
    localStorage.setItem('fh-home-layout', JSON.stringify(tiles));
    set({ tiles });
  },

  markSetupDone: () => {
    localStorage.setItem('fh-home-setup-done', 'true');
    set({ setupDone: true });
  },

  resetLayout: () => {
    localStorage.removeItem('fh-home-layout');
    localStorage.removeItem('fh-home-max-visible');
    set({ tiles: DEFAULT_TILES, maxVisible: null });
  },

  setMaxVisible: (n) => {
    if (n === null) localStorage.removeItem('fh-home-max-visible');
    else localStorage.setItem('fh-home-max-visible', String(n));
    set({ maxVisible: n });
  },
}));
